// TripCardFull — The full journey view, rendering a chronological timeline
// (Trip_Overview_Card + phase-grouped Trip_Steps) as the trip card body.
//
// Composes: NextActionBanner + TimelineView
// Wires: buildTimelineModel, nextActionEngine, buildJourneyEvents,
//         resolveTripStages, useCurrentTime
//
// Features:
//   - aria-live="assertive" region announces active-stage changes (feeds NextActionBanner)
//   - Completed trip summary view when all stages are done
//   - Max content width 1280px, centered horizontally when viewport exceeds that
//   - Mobile sticky actions and responsive padding/font sizing
//
// Validates: Requirements 1.1, 1.2, 1.4, 1.5, 8.1, 8.2, 8.3, 8.4, 9.1, 9.2, 9.3, 13.1, 13.2, 13.3, 13.4

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle } from 'lucide-react';

import {
  colors,
  typography,
  fontWeights,
  spacing,
  cardStyles,
  actionButtonStyle,
} from '../../styles/designTokens';

import { resolveTripStages, STAGES, STAGE_LABELS } from '../../services/journeyStageEngine';
import { computeNextAction } from '../../services/nextActionEngine';

import { buildJourneyEvents } from './buildJourneyEvents';
import { buildTimelineModel } from './buildTimelineModel';
import { TimelineView } from './timeline/TimelineView';
import { useCurrentTime } from './useCurrentTime';
import { NextActionBanner } from './NextActionBanner';
import { PrimaryGpsView } from './PrimaryGpsView';
import { DiningMomentCard } from './DiningMomentCard';
import { DiningMomentSheet } from './DiningMomentSheet';
import { detectDiningMoments } from './detectDiningMoments';
import { isDiningMomentDismissed, dismissDiningMoment } from './diningMomentDismissal';
import { formatDepartureTime } from './formatDepartureTime';
import TripActions from './TripActions';
import DeleteConfirmation from './DeleteConfirmation';
import EditTripModal from '../EditTripModal';
import { deleteTrip, saveTrip } from '../../services/trips';

// ── Responsive breakpoint constant ─────────────────────────
const DESKTOP_BREAKPOINT = 768;
const MAX_CONTENT_WIDTH = 1280;

// ── Constants ───────────────────────────────────────────────

/**
 * Hook to detect desktop viewport (≥768px).
 * Uses matchMedia for efficient, event-driven detection with no layout thrashing.
 * The breakpoint transition is CSS-driven so expand/collapse state is never lost.
 *
 * @returns {boolean} true when viewport width ≥ DESKTOP_BREAKPOINT
 */
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia
      ? window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`).matches
      : window.innerWidth >= DESKTOP_BREAKPOINT;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
    const handler = (e) => setIsDesktop(e.matches);
    // Modern browsers support addEventListener on MediaQueryList
    if (mql.addEventListener) {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    // Fallback for older browsers
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, []);

  return isDesktop;
}

// ── Helpers ─────────────────────────────────────────────────

/**
 * Normalize context maps for buildJourneyEvents, handling both multi-leg
 * map props and legacy single-leg fallbacks.
 */
function buildCtx({ trip, flightStatuses, departureCalculations, journeyDataMap }) {
  return {
    flightStatuses: flightStatuses || {},
    departureCalculations: departureCalculations || {},
    journeyDataMap: journeyDataMap || {},
  };
}

/**
 * Determines if a trip is fully completed (no active stage, all stages done).
 *
 * @param {Object|null} stageResult - Result from resolveTripStages
 * @returns {boolean}
 */
function isTripComplete(stageResult) {
  if (!stageResult) return false;
  const { activeStage } = stageResult;
  return activeStage && activeStage.stage === STAGES.TRIP_COMPLETE;
}

/** Outer card container style — desktop gets a centered max-width, mobile goes full-bleed. */
function getCardContainerStyle(isDesktop, isMobile) {
  return {
    ...cardStyles.container,
    position: 'relative',
    overflow: 'visible',
    maxWidth: isDesktop ? `${MAX_CONTENT_WIDTH}px` : 'none',
    marginLeft: isDesktop ? 'auto' : undefined,
    marginRight: isDesktop ? 'auto' : undefined,
    // Smooth layout transition at breakpoint (Req 10.3) — within 100ms
    transition: 'max-width 80ms ease-out',
    // Mobile: full-width single-column layout (Req 3.4, 10.1)
    ...(isMobile ? { width: '100%', borderRadius: 0 } : {}),
  };
}

/** Card body style — mobile adds bottom padding for the sticky footer and a minimum font size. */
function getCardBodyStyle(isMobile) {
  return {
    padding: isMobile ? '12px 16px' : spacing.cardPadding,
    // Mobile: add bottom padding to account for sticky action footer (Req 3.3)
    ...(isMobile ? { paddingBottom: '120px' } : {}),
    // Mobile: enforce minimum 14px font size (Req 10.4)
    ...(isMobile ? { fontSize: '14px' } : {}),
  };
}

// ── Sub-components ──────────────────────────────────────────

/**
 * Completed trip summary view (Req 1.5).
 * Shown when no active stage exists and all stages are completed.
 */
function CompletedTripSummary({ trip }) {
  const startDate = trip.startDate ? new Date(trip.startDate) : null;
  const endDate = trip.endDate ? new Date(trip.endDate) : null;

  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const dateRange = startDate && endDate
    ? `${dateFormatter.format(startDate)} – ${dateFormatter.format(endDate)}`
    : '';

  return (
    <div
      data-testid="completed-trip-summary"
      style={{
        textAlign: 'center',
        padding: '32px 16px',
      }}
    >
      <CheckCircle
        size={48}
        style={{ color: colors.success, margin: '0 auto 12px' }}
        aria-hidden="true"
      />
      <h2
        style={{
          fontSize: typography.heading.fontSize,
          fontWeight: fontWeights.bold,
          color: colors.textPrimary,
          margin: '0 0 8px',
        }}
      >
        {trip.tripName || 'Trip Complete'}
      </h2>
      {dateRange && (
        <p
          style={{
            fontSize: typography.body.fontSize,
            color: colors.textSecondary,
            margin: 0,
          }}
        >
          {dateRange}
        </p>
      )}
      <p
        style={{
          fontSize: typography.body.fontSize,
          color: colors.success,
          fontWeight: fontWeights.medium,
          marginTop: '12px',
        }}
        role="img"
        aria-label="Trip completed successfully"
      >
        ✓ Journey Complete
      </p>
    </div>
  );
}

/**
 * Mobile sticky action footer — primary action visible without scrolling (Req 3.3, 3.5).
 * Extracted to reduce cyclomatic complexity of TripCardFull.
 *
 * @param {{ onAddEvents: () => void }} props
 */
function MobileStickyActions({ onAddEvents }) {
  return (
    <div
      data-testid="mobile-sticky-actions"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: colors.surface,
        borderTop: `1px solid ${colors.divider}`,
        padding: '12px 16px',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
      }}
    >
      <button
        data-testid="mobile-action-add-events"
        onClick={onAddEvents}
        style={{
          ...actionButtonStyle,
          width: '100%',
          justifyContent: 'center',
          padding: '12px 16px',
          fontSize: '14px',
          minHeight: '44px',
          minWidth: '44px',
          backgroundColor: 'transparent',
          color: colors.success,
          borderColor: colors.success,
        }}
        title="Add events to this trip"
      >
        Add Events
      </button>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────

/**
 * @typedef {Object} TripCardFullProps
 * @property {Object} trip - Full trip object
 * @property {Object} userProfile - User profile with lounge memberships etc.
 * @property {Object} flightStatuses - Map of flightId → flightStatus
 * @property {Object} departureCalculations - Map of legIndex → departureCalc
 * @property {Object} journeyDataMap - Map of flightId → journeyData
 * @property {() => void} onBack - Navigate back to trips list
 * @property {(tripId: string) => void} onTripDeleted - Callback when trip is deleted
 */

/**
 * TripCardFull is the full journey view that composes the chronological
 * timeline (Trip_Overview_Card + phase-grouped Trip_Steps) into a cohesive
 * trip experience.
 *
 * @param {TripCardFullProps} props
 * @returns {React.ReactElement}
 */
export function TripCardFull({
  trip,
  userProfile,
  flightStatuses,
  departureCalculations,
  journeyDataMap,
  onBack,
  onTripDeleted,
  onTripUpdated,
}) {
  const now = useCurrentTime();
  const isDesktop = useIsDesktop();
  const isMobile = !isDesktop;
  const prevActiveStageRef = useRef(null);
  const [stageAnnouncement, setStageAnnouncement] = useState('');

  // ── Action state (Req 2.1, 2.3, 2.6, 2.8, 2.9, 3.1, 3.2) ──
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // ── View mode: GPS-style single-focus view (default) vs. full itinerary ──
  // GPS is the primary surface (Requirement: single "what's now / next"
  // view); the full always-expanded timeline stays one tap away, not
  // deleted — it's still useful for trip-planning/overview.
  const [viewMode, setViewMode] = useState('gps');
  useEffect(() => {
    setViewMode('gps');
  }, [trip?.tripId]);

  // ── Departure time display for departure-from-origin stage (Req 1.2, 1.4, 1.5, 1.6) ──
  const departureTimeDisplay = useMemo(() => {
    const calc = departureCalculations && departureCalculations[0];
    if (!calc) return null;
    if (calc.success === true && calc.leaveByTime) {
      const formatted = formatDepartureTime(
        calc.leaveByTime instanceof Date ? calc.leaveByTime : new Date(calc.leaveByTime)
      );
      return formatted; // null from formatDepartureTime means invalid date → treated as unavailable
    }
    // Any non-success entry (error, unavailable, or missing success flag)
    return null;
  }, [departureCalculations]);

  // ── Delete handler with 30s timeout and error recovery ──
  const handleConfirmDelete = useCallback(async () => {
    setDeleting(true);
    setDeleteError(null);

    const timeout = (ms) => new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Timeout')), ms)
    );

    try {
      await Promise.race([
        deleteTrip(trip.tripId, userProfile?.sub ?? trip.userId),
        timeout(30000),
      ]);
      onTripDeleted(trip.tripId);
    } catch (_err) {
      setDeleteError('Trip was not deleted');
      setDeleting(false);
    }
  }, [trip.tripId, trip.userId, userProfile, onTripDeleted]);

  // ── Edit modal save handler ─────────────────────────────
  const handleEditSave = useCallback(async (editedTrip) => {
    await saveTrip(editedTrip);
    onTripUpdated(editedTrip);
    setShowEditModal(false);
  }, [onTripUpdated]);

  // ── Resolve active stage ────────────────────────────────
  const stageResolution = useMemo(() => {
    if (!trip?.flights?.length) return null;
    try {
      const ctx = buildCtx({ trip, flightStatuses, departureCalculations, journeyDataMap });
      return resolveTripStages({
        trip,
        flightStatuses: ctx.flightStatuses,
        departureCalculations: ctx.departureCalculations,
        journeyDataMap: ctx.journeyDataMap,
        now,
      });
    } catch (_err) {
      return null;
    }
  }, [trip, flightStatuses, departureCalculations, journeyDataMap, now]);

  const activeStageId = useMemo(() => {
    if (!stageResolution?.activeStage) return null;
    const stage = stageResolution.activeStage.stage;
    // TRIP_COMPLETE and PRE_TRIP are not part of the eight canonical stages
    if (stage === STAGES.TRIP_COMPLETE || stage === STAGES.PRE_TRIP) return null;
    return stage;
  }, [stageResolution]);

  // ── Build journey events ────────────────────────────────
  const { events } = useMemo(() => {
    const ctx = buildCtx({ trip, flightStatuses, departureCalculations, journeyDataMap });
    return buildJourneyEvents(trip, now, ctx);
  }, [trip, flightStatuses, departureCalculations, journeyDataMap, now]);

  // ── Context maps for the timeline model (Req 3.7, 13.5) ──
  // Includes userProfile so security tips/clearances flow through.
  const ctx = useMemo(
    () => ({
      ...buildCtx({ trip, flightStatuses, departureCalculations, journeyDataMap }),
      userProfile,
    }),
    [trip, flightStatuses, departureCalculations, journeyDataMap, userProfile],
  );

  // ── Build the chronological timeline model (Req 8/9/13) ──
  // Recomputed on each useCurrentTime tick so step statuses transition; the
  // pure builder is total and never throws for any trip shape.
  const timelineModel = useMemo(
    () => buildTimelineModel(trip, ctx, now),
    [trip, ctx, now],
  );

  // ── Classify stage nodes ────────────────────────────────
  // (removed — events are no longer grouped into the 8 canonical stages;
  //  the chronological TimelineView renders phase-grouped steps instead.)

  // ── Compute next action ─────────────────────────────────
  const nextAction = useMemo(() => {
    const activeStage = stageResolution?.activeStage || null;
    return computeNextAction(activeStage, events, now);
  }, [stageResolution, events, now]);

  // ── Dining moment (Phase 3): proactively surface a dining suggestion
  // instead of requiring the traveler to think to open the chat assistant.
  // Only ever non-null during the AT_DESTINATION window (detectDiningMoments
  // enforces this itself). Gated behind `nextAction.isIdle` — never allowed
  // to outrank a real deadline-driven instruction like "head to your gate".
  const [showDiningSheet, setShowDiningSheet] = useState(false);
  const [dismissVersion, setDismissVersion] = useState(0);

  const diningMoment = useMemo(() => {
    const activeStage = stageResolution?.activeStage || null;
    return detectDiningMoments(trip, activeStage, events, now);
  }, [trip, stageResolution, events, now]);

  const diningMomentDismissed = useMemo(
    () => isDiningMomentDismissed(trip?.tripId, diningMoment),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trip?.tripId, diningMoment, dismissVersion],
  );

  const showDiningMomentCard = Boolean(diningMoment) && !diningMomentDismissed && nextAction.isIdle;

  const handleDismissDiningMoment = useCallback(() => {
    if (!trip?.tripId || !diningMoment) return;
    dismissDiningMoment(trip.tripId, diningMoment);
    setDismissVersion((v) => v + 1);
  }, [trip?.tripId, diningMoment]);

  // ── Announce active-stage changes via aria-live (feeds NextActionBanner) ──
  useEffect(() => {
    if (!activeStageId) return;

    const prevStage = prevActiveStageRef.current;
    if (prevStage && prevStage !== activeStageId) {
      const stageLabel = STAGE_LABELS[activeStageId] || activeStageId;
      const actionSummary = nextAction && !nextAction.isIdle
        ? `. ${nextAction.instruction}`
        : '';
      setStageAnnouncement(`Now in: ${stageLabel}${actionSummary}`);
    }

    prevActiveStageRef.current = activeStageId;
  }, [activeStageId, nextAction]);

  // ── Render ──────────────────────────────────────────────

  // Check if trip is complete
  const tripComplete = isTripComplete(stageResolution);

  return (
    <div
      data-testid="trip-card-full"
      style={getCardContainerStyle(isDesktop, isMobile)}
    >
      {/* aria-live="assertive" region for stage change announcements (Req 12.3) */}
      <div
        aria-live="assertive"
        aria-atomic="true"
        data-testid="stage-change-announcement"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {stageAnnouncement}
      </div>

      {/* Next Action Banner — sticky at top (Req 6.4). Only rendered standalone
          here for the completed-trip case and the demoted itinerary view;
          the GPS view (default) embeds its own copy inside PrimaryGpsView so
          the instruction is never shown twice. */}
      {(tripComplete || viewMode !== 'gps') && (
        <NextActionBanner
          instruction={nextAction.instruction}
          actionButton={nextAction.actionButton}
          isIdle={nextAction.isIdle}
          idleMessage={nextAction.idleMessage}
          departureTime={activeStageId === 'departure_from_origin' ? departureTimeDisplay : undefined}
        />
      )}

      {/* Card body — mobile: ≤16px horizontal padding (Req 3.4), min 14px font (Req 10.4) */}
      <div
        data-testid="trip-card-body"
        style={getCardBodyStyle(isMobile)}
      >
        {/* Trip Actions — desktop controls in card header area (Req 2.1, 3.1) */}
        <TripActions
          onAddEvents={() => setShowEditModal(true)}
          onDeleteTrip={() => setShowDeleteConfirm(true)}
        />

        {/* Delete Confirmation — inline panel below actions when visible (Req 2.2, 2.6, 2.8, 2.9) */}
        {showDeleteConfirm && (
          <div style={{ marginTop: '12px' }}>
            <DeleteConfirmation
              tripName={trip.tripName}
              deleting={deleting}
              error={deleteError}
              onConfirm={handleConfirmDelete}
              onCancel={() => { setShowDeleteConfirm(false); setDeleteError(null); }}
            />
          </div>
        )}

        {/* Completed trip summary (Req 1.5) */}
        {tripComplete ? (
          <CompletedTripSummary trip={trip} />
        ) : viewMode === 'gps' ? (
          <div style={{ marginTop: '12px' }}>
            <PrimaryGpsView
              activeStage={stageResolution?.activeStage || null}
              nextAction={nextAction}
              departureTimeDisplay={departureTimeDisplay}
              onOpenItinerary={() => setViewMode('itinerary')}
            >
              {showDiningMomentCard && (
                <DiningMomentCard
                  diningMoment={diningMoment}
                  onOpen={() => setShowDiningSheet(true)}
                  onDismiss={handleDismissDiningMoment}
                />
              )}
            </PrimaryGpsView>
          </div>
        ) : (
          <>
            <button
              type="button"
              data-testid="back-to-gps-link"
              onClick={() => setViewMode('gps')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                margin: '12px 0',
                padding: '6px 10px',
                border: 'none',
                background: 'transparent',
                color: colors.infoText,
                fontSize: typography.caption.fontSize,
                fontWeight: fontWeights.medium,
                cursor: 'pointer',
              }}
            >
              ← Back to current status
            </button>
            <TimelineView model={timelineModel} isMobile={isMobile} />
          </>
        )}
      </div>

      {/* Mobile sticky action footer — primary actions visible without scrolling (Req 3.3, 3.5) */}
      {isMobile && (
        <MobileStickyActions
          onAddEvents={() => setShowEditModal(true)}
        />
      )}

      {/* Edit Trip Modal — conditionally mounted (Req 3.1, 3.2, 3.6, 3.8, 5.6) */}
      {showEditModal && (
        <EditTripModal
          trip={trip}
          onClose={() => setShowEditModal(false)}
          onSave={handleEditSave}
        />
      )}

      {/* Dining Moment Sheet — same isOpen-gated mount pattern as EditTripModal */}
      <DiningMomentSheet
        isOpen={showDiningSheet}
        onClose={() => setShowDiningSheet(false)}
        diningMoment={diningMoment}
        trip={trip}
        userProfile={userProfile}
        onReservationCreated={() => setShowDiningSheet(false)}
      />
    </div>
  );
}

export default TripCardFull;
